import { com } from '@eidu/web'
import useHasPermission from '../../../../permission/useHasPermission'
import { KindViewPageProps } from '../../KindPages'
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import assetResourceLinkKey = com.eidu.sharedlib.entity.assetResourceLinkKey
import assetRuntimeLinkKey = com.eidu.sharedlib.entity.assetRuntimeLinkKey
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import PublishableEntityKinds = com.eidu.sharedlib.entity.publish.PublishableEntityKinds

export type IsPublishableResult = {
  publishable: boolean
  reason?: string
  loading: boolean
}

/**
 * Returns whether the current user can publish the given asset. Requires: `PublishAsset` permission, a publishable
 * entity kind, and either an `education.trellis.asset.runtime` link (assets) or an `education.trellis.asset.resource`
 * link (runtimes — they are themselves the runtime, but must carry a payload).
 */
const useIsPublishable = (
  entity: KindViewPageProps['entity'],
  entityType: KindViewPageProps['entityType']
): IsPublishableResult => {
  const { hasPermission, loading } = useHasPermission(entity.entity.id, EntityAction.PublishAsset)

  if (loading) return { publishable: false, loading: true }
  if (!hasPermission) return { publishable: false, reason: 'Missing PublishAsset permission', loading: false }
  if (!PublishableEntityKinds.asJsReadonlySetView().has(entityType.kind))
    return { publishable: false, reason: `Entity kind ${entityType.kind} is not publishable`, loading: false }
  const links = entity.entity.links.asJsReadonlyMapView()
  if (entityType.kind.equals(EntityTypeKind.Companion.Runtime)) {
    if (!links.get(assetResourceLinkKey))
      return { publishable: false, reason: 'Runtime is missing an asset.resource payload link', loading: false }
  } else if (!links.get(assetRuntimeLinkKey)) {
    return { publishable: false, reason: 'Entity is missing an asset.runtime link', loading: false }
  }

  return { publishable: true, loading: false }
}

export default useIsPublishable
